import React, { useCallback, useEffect, useRef, useState } from 'react';
import Button from '../../common/Button';
import { shopPaymentAPI } from '../../../services/api';
import { messageFromError } from './errorMessage';

const inputClass =
  'w-full min-h-touch border border-border-strong rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent';

export const money = (n) => {
  const v = Number(n) || 0;
  return `${v < 0 ? '-' : ''}$${Math.abs(v).toFixed(2)}`;
};

const METHOD_LABELS = {
  card_link: 'Card (online)',
  cash: 'Cash',
  terminal: 'Card (in shop)',
  cheque: 'Cheque',
  other: 'Other',
};
const KIND_LABELS = { refund: 'Refund', dispute: 'Dispute', reinstated: 'Dispute reversed' };

export const paymentLabel = (p) => {
  if (p.note === 'Unmarked paid') return 'Correction';
  if (KIND_LABELS[p.kind]) return KIND_LABELS[p.kind];
  return METHOD_LABELS[p.method] || 'Other';
};

// A payment's created_at carries an offset (+00:00), so Date shows it local.
const day = (iso) => (iso ? new Date(iso).toLocaleDateString() : '');

const RECORD_METHODS = [
  ['cash', 'Cash'],
  ['terminal', 'Card in shop'],
  ['cheque', 'Cheque'],
  ['other', 'Other'],
];

// What has been paid on this estimate or invoice, plus a form for money the
// shop took itself. Renders nothing when the feature is off (the endpoint 404s).
const PaymentsPanel = ({ jobId, documentType, onChange }) => {
  const [summary, setSummary] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('cash');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const mounted = useRef(true);
  const inFlight = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const load = useCallback(async () => {
    setHidden(false);
    setSummary(null);
    setLoadError('');
    try {
      const data = await shopPaymentAPI.getPayments(jobId);
      if (!mounted.current) return;
      setSummary(data);
      setLoadError('');
    } catch (err) {
      if (!mounted.current) return;
      if (err?.response?.status === 404) setHidden(true);
      else setLoadError(messageFromError(err, 'Could not load payments.'));
    }
  }, [jobId]);

  useEffect(() => { load(); }, [load]);

  if (hidden) return null;

  const amountValue = parseFloat(amount);
  const tooPrecise = amountValue > 0
    && Math.abs(Math.round(amountValue * 100) - Number((amountValue * 100).toPrecision(12))) > 1e-9;
  const canSubmit = amountValue > 0 && !submitting;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit || inFlight.current) return;
    if (tooPrecise) {
      setError('Enter an amount in dollars and cents');
      return;
    }
    inFlight.current = true;
    setSubmitting(true);
    setError('');
    try {
      const body = { amount: Math.round(amountValue * 100) / 100, method };
      if (note.trim()) body.note = note.trim();
      const data = await shopPaymentAPI.recordPayment(jobId, body);
      if (!mounted.current) return;
      setSummary(data);
      setAmount('');
      setNote('');
      setMethod('cash');
      setOpen(false);
      if (onChange) onChange(data);
    } catch (err) {
      if (mounted.current) setError(messageFromError(err, 'Could not record the payment. Try again.'));
    } finally {
      inFlight.current = false;
      if (mounted.current) setSubmitting(false);
    }
  };

  const payments = summary?.payments || [];

  return (
    <div className="mb-6 border border-border-default rounded-md p-4">
      <h4 className="font-semibold text-ink mb-2">Payments</h4>

      {loadError && <p role="alert" className="text-small text-danger mb-2">{loadError}</p>}
      {!summary && !loadError && <p className="text-small text-ink-muted">Loading…</p>}

      {summary && (
        <>
          {payments.length === 0 ? (
            <p className="text-sm text-ink-muted">No payments yet.</p>
          ) : (
            <ul className="space-y-2">
              {payments.map((p) => (
                <li key={p.id} className="text-sm text-ink">
                  <span>{`${day(p.created_at)} · ${paymentLabel(p)} · ${money(p.amount)}`}</span>
                  {p.note && p.note !== 'Unmarked paid' && p.note !== 'Marked paid' && (
                    <span className="text-ink-muted">{` — ${p.note}`}</span>
                  )}
                  {p.mismatch && (
                    <p className="text-small text-warning bg-warning-tint rounded px-2 py-1 mt-1">
                      {`Customer paid ${money(p.amount)}; expected ${money(p.expected_amount)} — check Stripe`}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )}

          <p className="text-sm font-medium text-ink mt-3">
            {`Paid ${money(summary.amount_paid)} · Balance ${money(summary.balance_due)}`}
          </p>
          {summary.overpaid_by > 0 && (
            <p role="status" className="text-sm text-warning bg-warning-tint rounded px-2 py-1 mt-2">
              {`Overpaid by ${money(summary.overpaid_by)} — refund the difference in your Stripe dashboard`}
            </p>
          )}
        </>
      )}

      {summary && !open && (
        <div className="mt-3">
          <Button variant="secondary" onClick={() => setOpen(true)}>Record a payment</Button>
        </div>
      )}

      {open && (
        <form onSubmit={submit} className="mt-3 space-y-3" aria-label="Record a payment">
          <p className="text-small text-ink-muted">
            {documentType === 'estimate'
              ? 'Cash, card machine or cheque taken as a deposit.'
              : 'Cash, card machine or cheque taken for this invoice.'}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block text-sm font-medium text-ink">
              <span className="block mb-1">Amount ($)</span>
              <input
                aria-label="Payment amount"
                type="number"
                inputMode="decimal"
                min="0.01"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className={inputClass}
              />
            </label>
            <label className="block text-sm font-medium text-ink">
              <span className="block mb-1">Method</span>
              <select
                aria-label="Payment method"
                value={method}
                onChange={(e) => setMethod(e.target.value)}
                className={inputClass}
              >
                {RECORD_METHODS.map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </label>
          </div>
          <label className="block text-sm font-medium text-ink">
            <span className="block mb-1">Note (optional)</span>
            <input
              aria-label="Payment note"
              type="text"
              maxLength={500}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className={inputClass}
            />
          </label>
          {error && <p role="alert" className="text-small text-danger">{error}</p>}
          <div className="flex gap-2">
            <Button type="submit" disabled={!canSubmit} loading={submitting}>Record payment</Button>
            <Button type="button" variant="ghost" disabled={submitting} onClick={() => { setOpen(false); setError(''); }}>
              Cancel
            </Button>
          </div>
        </form>
      )}
    </div>
  );
};

export default PaymentsPanel;
